/**
 * Theme controller for the PanelGenie web client.
 *
 * Resolution order:
 *   1. Explicit user choice stored in `localStorage.pg.theme`
 *      (one of `"light" | "dark"`).
 *   2. System preference via `prefers-color-scheme: dark` → dark.
 *   3. Fallback to `"dark"` (per design system — most inspection
 *      jobs happen in dim spaces where dark is preferred).
 *
 * When the user has *not* set an explicit override, the controller
 * also listens for live system-preference changes and updates the
 * `data-theme` attribute on `<html>`. Once the user picks
 * explicitly (via Settings), the system listener stops applying
 * automatically — the user's choice wins until they reset it.
 *
 * The active theme is applied by writing `data-theme="..."` on
 * `<html>`; the CSS in `@panelgenie/shared-ui/src/themes/` and the
 * `@theme inline` block in `global.css` do the rest.
 *
 * Legacy migration: prior builds stored `"modern"` / `"blueprint"`
 * in the same key. `readStored()` maps those forward to
 * `"light"` / `"dark"` and rewrites the stored value so the
 * migration is one-shot.
 */

export const THEMES = ["light", "dark"] as const;
export type ThemeName = (typeof THEMES)[number];

const STORAGE_KEY = "pg.theme";

function readStored(): ThemeName | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === "light" || raw === "dark") return raw;
    if (raw === "modern") {
      localStorage.setItem(STORAGE_KEY, "light");
      return "light";
    }
    if (raw === "blueprint") {
      localStorage.setItem(STORAGE_KEY, "dark");
      return "dark";
    }
    return null;
  } catch {
    return null;
  }
}

function systemPrefersLight(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  return window.matchMedia("(prefers-color-scheme: light)").matches;
}

/**
 * Resolve the theme to apply on boot, honoring user override else
 * system preference. Defaults to dark when neither signal is
 * available. Pure of side effects (beyond the one-shot migration
 * in `readStored`).
 */
export function resolveInitialTheme(): ThemeName {
  const stored = readStored();
  if (stored !== null) return stored;
  return systemPrefersLight() ? "light" : "dark";
}

/**
 * Apply a theme by setting `data-theme` on the document root and
 * notifying subscribers. Both `useTheme` consumers and any other
 * watchers (system-preference listener, settings switcher) flow
 * through this single function — there's one source of truth and
 * one notification path.
 */
export function applyTheme(theme: ThemeName): void {
  document.documentElement.setAttribute("data-theme", theme);
  for (const listener of themeListeners) listener();
}

/**
 * Returns the active theme by reading `data-theme` off the document
 * root. Safe to call before React hydrates (used as the
 * `getServerSnapshot` for `useSyncExternalStore`); falls back to
 * `"dark"` when the attribute is missing or unknown.
 */
export function readActiveTheme(): ThemeName {
  if (typeof document === "undefined") return "dark";
  const raw = document.documentElement.getAttribute("data-theme");
  return raw === "light" || raw === "dark" ? raw : "dark";
}

const themeListeners = new Set<() => void>();

/**
 * Subscribe to theme changes. The listener fires after every
 * `applyTheme` call — including the one inside `watchSystemTheme`
 * — so React consumers stay in sync without their own state.
 */
export function subscribeToTheme(listener: () => void): () => void {
  themeListeners.add(listener);
  return () => themeListeners.delete(listener);
}

/**
 * Persist the user's explicit choice (or clear it to fall back to
 * system preference).
 */
export function storeTheme(theme: ThemeName | null): void {
  try {
    if (theme === null) {
      localStorage.removeItem(STORAGE_KEY);
    } else {
      localStorage.setItem(STORAGE_KEY, theme);
    }
  } catch {
    /* localStorage unavailable; we just don't persist */
  }
}

/**
 * Returns true if the active theme was chosen explicitly by the
 * user (vs. derived from system preference). Used by the Settings
 * switcher to render the right radio state.
 */
export function isUserOverride(): boolean {
  return readStored() !== null;
}

/**
 * Subscribe to system-preference changes. The handler fires
 * whenever `(prefers-color-scheme: dark)` flips, but ONLY if the
 * user has not pinned an explicit choice. Returns an unsubscribe
 * function.
 */
export function watchSystemTheme(
  onChange: (next: ThemeName) => void,
): () => void {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return () => undefined;
  }
  const mq = window.matchMedia("(prefers-color-scheme: dark)");
  const handler = (e: MediaQueryListEvent) => {
    if (readStored() !== null) return;
    onChange(e.matches ? "dark" : "light");
  };
  mq.addEventListener("change", handler);
  return () => mq.removeEventListener("change", handler);
}
